// 1. Imports
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    TextField,
    Typography,
    Button,
    Grid2,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Switch,
    Tooltip,
    Alert,
    AlertTitle,
    Select,
    MenuItem,
    Chip,
    Divider,
} from "@mui/material";
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { When } from "react-if";
import dayjs, { Dayjs } from 'dayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import WifiIcon from '@mui/icons-material/Wifi';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import { toast } from 'react-toastify';
import Loading from "../../../loading";
import { RootState, store } from "../../../../store";
import { MessageCenterService } from "../../../../services/message-center.service";
import { ComposeSearchService } from "../../../../services/compose-search.service";
import { useParams } from "react-router-dom";
import { InfoOutlined } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import { GT_COLORS } from "../../../../constants/designTokens.constants";
import { SEND_TIME_OPTIONS, TIMEZONE_OPTIONS, defaultTimezone, legacySendTimesFor } from "../../../../constants/messageReminders.constants";

// 2. Component Def
interface Props {
    mode?: 'message' | 'reminder';
}

export const NewMessageComposeComponent: React.FC<Props> = ({ mode = 'message' }) => {
    // 2.1 State and Vars
    const navigate = useNavigate();
    const { id } = useParams();

    // Redux selectors
    const { formState, automatedReminderState, editMode, loading, selectedUserIds, recipientCriteria } = useSelector((state: RootState) => state.messageCenter);
    const { authUser } = useSelector((state: RootState) => state.auth);

    // Redux dispatch
    const { updateFormState, updateAutomatedReminderState, resetAll } = store.dispatch.messageCenter;

    // Unified state helpers
    const title = mode === 'reminder' ? automatedReminderState.title : formState.title;
    const body = mode === 'reminder' ? automatedReminderState.body : formState.body;
    const channels = mode === 'reminder' ? automatedReminderState.channels : formState.channels;

    const updateTitle = (val: string) => mode === 'reminder'
        ? updateAutomatedReminderState({ title: val })
        : updateFormState({ title: val });
    const updateBody = (val: string) => mode === 'reminder'
        ? updateAutomatedReminderState({ body: val })
        : updateFormState({ body: val });

    // Local state for schedule modal and date/time pickers
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [tempScheduledDate, setTempScheduledDate] = useState<Dayjs | null>(
        formState.scheduledDate ? dayjs(formState.scheduledDate) : dayjs()
    );
    const [tempScheduledTime, setTempScheduledTime] = useState<Dayjs | null>(dayjs());
    const [recipientCount, setRecipientCount] = useState<number | null>(null);
    const deliveryMethods = [
        { id: 'SMS', label: 'SMS', description: mode === 'reminder' ? 'Send via text message' : 'Send via text message' },
        { id: 'PUSH', label: 'Push Notification', description: mode === 'reminder' ? 'Send via app notification' : 'Send via app notification' }
    ];

    // Days of week for reminder cadence
    const daysOfWeek = [
        { short: 'S', full: 'SUN' },
        { short: 'M', full: 'MON' },
        { short: 'T', full: 'TUE' },
        { short: 'W', full: 'WED' },
        { short: 'TH', full: 'THU' },
        { short: 'F', full: 'FRI' },
        { short: 'SA', full: 'SAT' }
    ];

    // 2.2 Effects

    // Initialize form state from Redux or set defaults
    useEffect(() => {
        if (mode === 'reminder') {
            if (!automatedReminderState.title && !automatedReminderState.body) {
                const defaults = getDefaultReminderContent('CHARGING');
                updateAutomatedReminderState({
                    channels: ['PUSH'],
                    reminderType: 'CHARGING',
                    title: defaults.title,
                    body: defaults.body,
                    // Seed the schedule with the admin's own timezone; the
                    // store default (America/New_York) only applies when
                    // detection fails or the zone isn't in the curated list.
                    timezone: defaultTimezone(),
                });
            }
        } else {
            if (!formState.title && !formState.body) {
                updateFormState({
                    channels: ['PUSH'],
                    messageType: 'IMMEDIATE',
                });
            }
        }
    }, []);

    // Initialize edit mode if we have an ID parameter and not already editing
    useEffect(() => {
        if (id && !editMode.isEditing) {
            if (mode === 'reminder') {
                MessageCenterService.initializeAutomatedReminderEditMode(id, (isValid, message) => {
                    if (!isValid) {
                        toast.error(message || 'Failed to load reminder for editing');
                        navigate('/messages');
                    }
                });
            } else {
                MessageCenterService.initializeMessageEditMode(id, (isValid, message) => {
                    if (!isValid) {
                        toast.error(message || 'Failed to load message for editing');
                        navigate('/messages');
                    }
                });
            }
        }
    }, [id, editMode.isEditing]);

    // Resolve recipient count on mount
    useEffect(() => {
        const hasCriteria =
            (recipientCriteria.groupIds?.length ?? 0) > 0 ||
            (recipientCriteria.teamIds?.length ?? 0) > 0 ||
            (recipientCriteria.userIds?.length ?? 0) > 0 ||
            recipientCriteria.selectAllUsers === true;

        if (hasCriteria) {
            ComposeSearchService.count(recipientCriteria)
                .then(res => setRecipientCount(res.count))
                .catch(() => setRecipientCount(selectedUserIds.length));
        } else {
            setRecipientCount(selectedUserIds.length);
        }
    }, []);

    // 2.3 Functions

    const handleDeliveryMethodToggle = (methodId: string) => {
        const newMethods = channels.includes(methodId)
            ? channels.filter(id => id !== methodId)
            : [...channels, methodId];

        if (mode === 'reminder') {
            updateAutomatedReminderState({ channels: newMethods });
        } else {
            updateFormState({ channels: newMethods });
        }
    };

    const handleScheduleMessage = () => {
        updateFormState({ messageType: 'SCHEDULED' });
        setShowScheduleModal(true);
    };

    const handleScheduleConfirm = () => {
        if (tempScheduledDate && tempScheduledTime) {
            const combinedDateTime = tempScheduledDate
                .hour(tempScheduledTime.hour())
                .minute(tempScheduledTime.minute())
                .second(0)
                .millisecond(0);

            if (!combinedDateTime.isValid()) {
                toast.error('Invalid date or time selected');
                return;
            }

            updateFormState({
                scheduledDate: combinedDateTime.toISOString(),
            });
        }
        setShowScheduleModal(false);
    };

    // --- Reminder-specific helpers ---

    const getDefaultReminderContent = (type: 'CHARGING' | 'STAY_CONNECTED' | 'UPDATE_REMINDER') => {
        switch (type) {
            case 'CHARGING':
                return {
                    title: 'Charge Your POM Device',
                    body: 'Hey! Your POM device battery is running low. Please plug it in to charge so it stays ready to keep you safe.',
                };
            case 'STAY_CONNECTED':
                return {
                    title: 'Stay Connected Reminder',
                    body: 'We noticed your POM app or device has been offline. Please open the app and reconnect so we can keep you safe.',
                };
            case 'UPDATE_REMINDER':
                return {
                    title: 'Update Your POM App',
                    body: 'A new version of the POM app is available. Please update to the latest version for the best experience.',
                };
        }
    };

    const handleReminderTypeSelect = (type: 'CHARGING' | 'STAY_CONNECTED' | 'UPDATE_REMINDER') => {
        const defaults = getDefaultReminderContent(type);
        if (!editMode.isEditing) {
            updateAutomatedReminderState({ reminderType: type, title: defaults.title, body: defaults.body });
        } else {
            updateAutomatedReminderState({ reminderType: type });
        }
    };

    const handleDayToggle = (day: string) => {
        const currentDays = automatedReminderState.daysOfWeek;
        const updatedDays = currentDays.includes(day)
            ? currentDays.filter(d => d !== day)
            : [...currentDays, day];
        updateAutomatedReminderState({ daysOfWeek: updatedDays });
    };

    const getReminderDetails = () => {
        switch (automatedReminderState.reminderType) {
            case 'CHARGING':
                return {
                    title: 'Charging Reminder',
                    description: "When an automated check is run (based on the selected cadence below) all users whose POM Device battery is below 25% will receive your message reminding them to charge",
                };
            case 'STAY_CONNECTED':
                return {
                    title: 'Stay Connected',
                    description: "Automated checks run at the send time you choose below",
                    improvementLabel: "Users whose Mobile App or POM Device has been offline for more than the period of time specified below will receive your message reminding them to reconnect"
                };
            case 'UPDATE_REMINDER':
                return {
                    title: 'Update Reminder',
                    description: "When an automated check is run (based on the selected cadence below) all users who are currently using an outdated version of the app will receive your message reminding them to update"
                };
            default:
                return null;
        }
    };

    // --- Send-time schedule handlers (ClickUp 868jmp2ge) ---

    // For Stay Connected, intervalHours is the admin-chosen inactivity
    // threshold. Charging/Update have no threshold concept — the field only
    // survives for API compatibility, so derive it from the send count
    // (two times = the old "every 12 hours", one = daily).
    const effectiveIntervalHours = () =>
        automatedReminderState.reminderType === 'STAY_CONNECTED'
            ? automatedReminderState.intervalHours
            : (automatedReminderState.sendTimes.length === 2 ? 12 : 24);

    // Mirrors the API rule: two send times must be >= 60 minutes apart
    // (circular — 11:45 PM and 12:15 AM are 30 minutes apart), because the
    // tick's grace window would deliver the second one late.
    const minutesOf = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
    const circularGapMin = (a: string, b: string) => {
        const d = Math.abs(minutesOf(a) - minutesOf(b));
        return Math.min(d, 1440 - d);
    };

    const updateSendTime = (index: number, value: string) => {
        const times = [...automatedReminderState.sendTimes];
        if (times.some((t, i) => i !== index && circularGapMin(t, value) < 60)) {
            toast.error('Send times must be at least 1 hour apart');
            return;
        }
        times[index] = value;
        updateAutomatedReminderState({ sendTimes: times });
    };

    const addSendTime = () => {
        if (automatedReminderState.sendTimes.length >= 2) return;
        const [first] = automatedReminderState.sendTimes;
        // Offer the opposite half of the day so morning/evening pairs are
        // one click ('08:00' -> '20:00').
        const [h, m] = (first ?? '08:00').split(':').map(Number);
        const opposite = `${String((h + 12) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        updateAutomatedReminderState({ sendTimes: [...automatedReminderState.sendTimes, opposite] });
    };

    const removeSendTime = (index: number) => {
        if (automatedReminderState.sendTimes.length <= 1) return;
        updateAutomatedReminderState({
            sendTimes: automatedReminderState.sendTimes.filter((_, i) => i !== index),
        });
    };

    // --- Submit handlers ---

    const handleSendMessage = async () => {
        if (!isFormValid()) return;

        let scheduledDateTime: Date | null = null;

        if (formState.messageType === 'SCHEDULED') {
            if (formState.scheduledDate) {
                scheduledDateTime = new Date(formState.scheduledDate);

                if (isNaN(scheduledDateTime.getTime())) {
                    toast.error('Invalid scheduled date/time');
                    return;
                }

                if (scheduledDateTime <= new Date()) {
                    toast.error('Scheduled time must be in the future');
                    return;
                }
            } else {
                toast.error('Please select a date and time for scheduling');
                return;
            }
        }

        if (editMode.isEditing && editMode.batchId) {
            await MessageCenterService.updateMessage(
                editMode.batchId,
                formState.title,
                formState.body,
                selectedUserIds,
                formState.channels,
                formState.messageType,
                formState.isAnnouncement,
                formState.isCritical,
                scheduledDateTime,
                (isValid, message, result) => {
                    if (isValid) {
                        toast.success('Message updated successfully');
                        resetAll();
                        navigate('/messages');
                    } else {
                        toast.error(message || 'Failed to update message');
                    }
                },
                recipientCriteria,
            );
        } else {
            await MessageCenterService.createMessage(
                formState.title,
                formState.body,
                selectedUserIds,
                formState.channels,
                formState.messageType,
                formState.isAnnouncement,
                formState.isCritical,
                scheduledDateTime,
                (isValid, message, result) => {
                    if (isValid) {
                        toast.success(
                            formState.messageType === 'IMMEDIATE'
                                ? 'Message sent successfully'
                                : 'Message scheduled successfully'
                        );
                        resetAll();
                        navigate('/messages');
                    } else {
                        toast.error(message || 'Failed to send message');
                    }
                },
                recipientCriteria,
            );
        }
    };

    const handleSetReminder = async () => {
        if (!automatedReminderState.reminderType) {
            toast.error('Please select a reminder type');
            return;
        }
        if (!automatedReminderState.title.trim()) {
            toast.error('Please enter a reminder name');
            return;
        }
        if (!automatedReminderState.body.trim()) {
            toast.error('Please enter a message');
            return;
        }
        if (automatedReminderState.channels.length === 0) {
            toast.error('Please select at least one delivery method');
            return;
        }
        if (automatedReminderState.daysOfWeek.length === 0) {
            toast.error('Please select at least one day of the week');
            return;
        }

        if (editMode.isEditing && editMode.reminderId) {
            await MessageCenterService.updateAutomatedReminder(
                editMode.reminderId,
                automatedReminderState.title,
                automatedReminderState.body,
                selectedUserIds,
                automatedReminderState.channels,
                automatedReminderState.reminderType,
                effectiveIntervalHours(),
                automatedReminderState.daysOfWeek,
                true,
                (isValid, message) => {
                    if (isValid) {
                        toast.success('Reminder updated successfully');
                        resetAll();
                        navigate('/messages');
                    } else {
                        toast.error(message || 'Failed to update reminder');
                    }
                },
                recipientCriteria,
                { sendTimes: automatedReminderState.sendTimes, timezone: automatedReminderState.timezone, timezoneStrategy: automatedReminderState.timezoneStrategy }
            );
        } else {
            await MessageCenterService.createAutomatedReminder(
                automatedReminderState.title,
                automatedReminderState.body,
                selectedUserIds,
                automatedReminderState.channels,
                automatedReminderState.reminderType,
                effectiveIntervalHours(),
                automatedReminderState.daysOfWeek,
                (isValid, message) => {
                    if (isValid) {
                        toast.success('Reminder created successfully');
                        resetAll();
                        navigate('/messages');
                    } else {
                        toast.error(message || 'Failed to create reminder');
                    }
                },
                recipientCriteria,
                { sendTimes: automatedReminderState.sendTimes, timezone: automatedReminderState.timezone, timezoneStrategy: automatedReminderState.timezoneStrategy }
            );
        }
    };

    const handleSubmit = () => {
        if (mode === 'reminder') {
            handleSetReminder();
        } else {
            handleSendMessage();
        }
    };

    const isFormValid = () => {
        const hasRecipients = selectedUserIds.length > 0 ||
            (recipientCriteria.groupIds?.length ?? 0) > 0 ||
            (recipientCriteria.teamIds?.length ?? 0) > 0 ||
            (recipientCriteria.userIds?.length ?? 0) > 0 ||
            recipientCriteria.selectAllUsers === true;

        if (mode === 'reminder') {
            return (
                !!automatedReminderState.reminderType &&
                automatedReminderState.title.trim() !== '' &&
                automatedReminderState.body.trim() !== '' &&
                automatedReminderState.channels.length > 0 &&
                automatedReminderState.daysOfWeek.length > 0 &&
                hasRecipients
            );
        }

        const basicValidation = formState.title.trim() !== '' &&
               formState.body.trim() !== '' &&
               formState.channels.length > 0 &&
               hasRecipients;

        if (formState.messageType === 'SCHEDULED') {
            return basicValidation && formState.scheduledDate !== null;
        }

        return basicValidation;
    };

    const getScheduledDateDisplay = () => {
        if (formState.scheduledDate) {
            return dayjs(formState.scheduledDate).format('MMM DD, YYYY');
        }
        return 'Schedule';
    };

    const getScheduledTimeDisplay = () => {
        if (formState.scheduledDate) {
            return dayjs(formState.scheduledDate).format('h:mm A');
        }
        return null;
    };

    const handleBack = () => {
        if (mode === 'reminder') {
            if (editMode.isEditing && id) {
                navigate(`/messages/automated-reminders/${id}/edit/recipients`);
            } else {
                navigate('/messages/automated-reminders/recipients');
            }
        } else {
            if (editMode.isEditing) {
                navigate(`/messages/${id}/edit/recipients`);
            } else {
                navigate('/messages/new/recipients');
            }
        }
    };

    const getActionButtonText = (): string => {
        if (mode === 'reminder') {
            return editMode.isEditing ? 'Update Reminder' : 'Set Reminder';
        }
        if (editMode.isEditing) {
            return formState.messageType === 'IMMEDIATE' ? 'Send Now' : 'Save Changes';
        } else {
            return formState.messageType === 'IMMEDIATE' ? 'Send Now' : 'Schedule Message';
        }
    };

    const reminderDetails = mode === 'reminder' ? getReminderDetails() : null;

    // 2.4 JSX Return
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ position: "relative" }}>
                {/* Loading Overlay */}
                <When condition={loading.createMessage || loading.updateMessage}>
                    <Box sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                    }}>
                        <Loading />
                    </Box>
                </When>

                {/* Page Header */}
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 1 }}>
                    {mode === 'reminder' ? 'Compose and Schedule Your Reminder' : 'Compose and Schedule Your Message'}
                </Typography>

                {/* Recipients Summary */}
                <Card elevation={0} sx={{ mb: 3, backgroundColor: GT_COLORS.pageBg, border: `1px solid ${GT_COLORS.border}`, borderRadius: 2.5 }}>
                    <CardContent>
                        <Typography variant="body1">
                            {mode === 'reminder' ? (
                                <>
                                    This Automated Reminder is monitoring the activity for{' '}
                                    <strong>{recipientCount ?? selectedUserIds.length} POM Users</strong>
                                    {' '}and will send a reminder if the criteria is met.
                                </>
                            ) : (
                                <>
                                    You have selected <strong>{recipientCount ?? selectedUserIds.length} POM Users</strong> as part of this message campaign.
                                </>
                            )}
                        </Typography>
                    </CardContent>
                </Card>

                {/* === REMINDER-SPECIFIC SECTIONS === */}
                <When condition={mode === 'reminder'}>
                    {/* Reminder Type Selection */}
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 2 }}>
                        Select the type of Reminder you would like to send
                    </Typography>

                    <Grid2 container spacing={2} sx={{ mb: 3 }}>
                        <Grid2 size={3}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    border: automatedReminderState.reminderType === 'CHARGING' ? `2px solid ${GT_COLORS.primary}` : `1px solid ${GT_COLORS.border}`,
                                    backgroundColor: automatedReminderState.reminderType === 'CHARGING' ? GT_COLORS.primaryLight : 'white',
                                    '&:hover': { boxShadow: 2 }
                                }}
                                onClick={() => handleReminderTypeSelect('CHARGING')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                    <BatteryChargingFullIcon sx={{ fontSize: '2.5rem', color: GT_COLORS.textMuted, mb: 1 }} />
                                    <Typography variant="subtitle2" fontWeight={500}>
                                        Charging Reminder
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>

                        <Grid2 size={3}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    border: automatedReminderState.reminderType === 'UPDATE_REMINDER' ? `2px solid ${GT_COLORS.primary}` : `1px solid ${GT_COLORS.border}`,
                                    backgroundColor: automatedReminderState.reminderType === 'UPDATE_REMINDER' ? GT_COLORS.primaryLight : 'white',
                                    '&:hover': { boxShadow: 2 }
                                }}
                                onClick={() => handleReminderTypeSelect('UPDATE_REMINDER')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                    <SystemUpdateIcon sx={{ fontSize: '2.5rem', color: GT_COLORS.textMuted, mb: 1 }} />
                                    <Typography variant="subtitle2" fontWeight={500}>
                                        Update Reminder
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>

                        <Grid2 size={3}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    border: automatedReminderState.reminderType === 'STAY_CONNECTED' ? `2px solid ${GT_COLORS.primary}` : `1px solid ${GT_COLORS.border}`,
                                    backgroundColor: automatedReminderState.reminderType === 'STAY_CONNECTED' ? GT_COLORS.primaryLight : 'white',
                                    '&:hover': { boxShadow: 2 }
                                }}
                                onClick={() => handleReminderTypeSelect('STAY_CONNECTED')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                    <WifiIcon sx={{ fontSize: '2.5rem', color: GT_COLORS.textMuted, mb: 1 }} />
                                    <Typography variant="subtitle2" fontWeight={500}>
                                        Stay Connected
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>

                    {/* Reminder Details */}
                    {reminderDetails && (
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 1 }}>
                                Details
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    • {reminderDetails.description}
                                </Typography>
                                {reminderDetails.improvementLabel && (
                                    <Typography variant="body2">
                                        • {reminderDetails.improvementLabel}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Editing a dormant legacy reminder (matches neither legacy
                        sweep, so it never actually sends today): saving will
                        activate it at the times shown below — say so up front
                        instead of letting that happen silently. */}
                    <When condition={mode === 'reminder' && editMode.isEditing
                        && !editMode.originalReminder?.sendTimes?.length
                        && legacySendTimesFor(editMode.originalReminder?.reminderType, editMode.originalReminder?.intervalHours) === null}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <AlertTitle>This reminder is currently dormant</AlertTitle>
                            Its legacy configuration never matched a send cycle, so it has not been
                            sending. Saving will activate it on the schedule you choose below.
                        </Alert>
                    </When>

                    {/* Reminder Cadence */}
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 2 }}>
                            Reminder Cadence
                        </Typography>

                        {/* Inactivity threshold — a recipient filter, so Stay
                            Connected only. Charging/Update frequency is simply
                            how many send times are configured below. */}
                        {automatedReminderState.reminderType === 'STAY_CONNECTED' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pl: 2 }}>
                                <Typography variant="body2">• Send reminder to users who have been inactive for at least:</Typography>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={`${automatedReminderState.intervalHours} HOURS`}
                                        onChange={(e) => {
                                            const [value] = e.target.value.split(' ');
                                            updateAutomatedReminderState({
                                                intervalHours: parseInt(value),
                                            });
                                        }}
                                    >
                                        <MenuItem value="12 HOURS">12 Hours</MenuItem>
                                        <MenuItem value="24 HOURS">24 Hours</MenuItem>
                                        <MenuItem value="48 HOURS">48 Hours</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pl: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2">• Send reminder at:</Typography>
                            {automatedReminderState.sendTimes.map((time, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <Select
                                            value={time}
                                            onChange={(e) => updateSendTime(index, e.target.value as string)}
                                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                                        >
                                            {SEND_TIME_OPTIONS.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {automatedReminderState.sendTimes.length > 1 && (
                                        <CloseIcon
                                            fontSize="small"
                                            data-testid={`remove-send-time-${index}`}
                                            sx={{ cursor: 'pointer', color: GT_COLORS.textMuted }}
                                            onClick={() => removeSendTime(index)}
                                        />
                                    )}
                                </Box>
                            ))}
                            {automatedReminderState.sendTimes.length < 2 && (
                                <Chip
                                    icon={<AddIcon />}
                                    label="Add second time"
                                    variant="outlined"
                                    onClick={addSendTime}
                                    sx={{ cursor: 'pointer', borderColor: GT_COLORS.borderInput, color: GT_COLORS.textMuted }}
                                />
                            )}
                            <Typography variant="body2">in</Typography>
                            {/* One picker answers "whose clock?": a concrete
                                zone (fixed strategy) or each user's own local
                                time (perRecipient, ClickUp 868kpzqcd). The
                                stored `timezone` survives an auto selection —
                                it becomes the fallback below, and the API
                                requires it either way. */}
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <Select
                                    value={automatedReminderState.timezoneStrategy === 'perRecipient' ? 'perRecipient' : automatedReminderState.timezone}
                                    data-testid="timezone-strategy-select"
                                    onChange={(e) => {
                                        const value = e.target.value as string;
                                        if (value === 'perRecipient') {
                                            updateAutomatedReminderState({ timezoneStrategy: 'perRecipient' });
                                        } else {
                                            updateAutomatedReminderState({ timezoneStrategy: 'fixed', timezone: value });
                                        }
                                    }}
                                >
                                    <MenuItem value="perRecipient">Each user&apos;s own local time (auto)</MenuItem>
                                    <Divider />
                                    {TIMEZONE_OPTIONS.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {automatedReminderState.timezoneStrategy === 'perRecipient' && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, pl: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="body2">• Fallback time zone, for users without a known location:</Typography>
                                    <FormControl size="small" sx={{ minWidth: 170 }}>
                                        <Select
                                            value={automatedReminderState.timezone}
                                            data-testid="fallback-timezone-select"
                                            onChange={(e) => updateAutomatedReminderState({ timezone: e.target.value as string })}
                                        >
                                            {TIMEZONE_OPTIONS.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', pl: 4, mb: 2, color: GT_COLORS.textMuted }}>
                                    Each user receives the reminder at this time in their own time zone,
                                    based on their last known location.
                                </Typography>
                            </>
                        )}

                        <Box sx={{ pl: 2 }}>
                            <Typography variant="body2">• Only send reminder on specific days of the week:</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 4 }}>
                                {daysOfWeek.map(day => (
                                    <Chip
                                        key={day.full}
                                        label={day.short}
                                        onClick={() => handleDayToggle(day.full)}
                                        variant={automatedReminderState.daysOfWeek.includes(day.full) ? "filled" : "outlined"}
                                        sx={{
                                            minWidth: 40,
                                            cursor: 'pointer',
                                            ...(automatedReminderState.daysOfWeek.includes(day.full)
                                                ? { backgroundColor: GT_COLORS.primary, color: 'white', '&:hover': { backgroundColor: GT_COLORS.primaryHover } }
                                                : { borderColor: GT_COLORS.borderInput, color: GT_COLORS.textMuted }),
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </When>

                {/* Delivery Method Selection */}
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 2 }}>
                    Select a Delivery Method
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: GT_COLORS.textMuted, mb: 3 }}>
                    Choose whether to send the message as an SMS, a push notification, or both
                </Typography>

                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', mb: 4 }}>
                    {deliveryMethods.map((method) => (
                        <FormControlLabel
                            key={method.id}
                            control={
                                <Checkbox
                                    checked={channels.includes(method.id)}
                                    onChange={() => handleDeliveryMethodToggle(method.id)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1" fontWeight={500}>
                                        {method.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                                        {method.description}
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start', ml: 0 }}
                        />
                    ))}
                </Box>

                {/* Push notification info alert */}
                <When condition={channels.includes('PUSH')}>
                    <Alert
                        severity="info"
                        icon={<InfoOutlined />}
                        sx={{
                            mb: 4,
                            backgroundColor: GT_COLORS.primaryLight,
                            border: `1px solid ${GT_COLORS.primary}`,
                            '& .MuiAlert-icon': {
                                color: GT_COLORS.primary
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 500, color: GT_COLORS.textPrimary }}>
                            Push Notification Selected
                        </AlertTitle>
                        <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                            When sending push notifications, your {mode === 'reminder' ? 'Reminder Name' : 'Campaign Name'} will appear as the notification title on recipients' devices. Choose a clear, professional title.
                        </Typography>
                    </Alert>
                </When>

                {/* Title Field */}
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 1 }}>
                    {mode === 'reminder' ? 'Reminder Name' : 'Campaign Name'}
                </Typography>
                <TextField
                    placeholder="Title"
                    fullWidth
                    value={title}
                    onChange={(e) => updateTitle(e.target.value)}
                    sx={{ mb: 4 }}
                />

                {/* Message Composition */}
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 1 }}>
                    Compose Message
                </Typography>
                <TextField
                    placeholder="Message Content"
                    fullWidth
                    multiline
                    rows={mode === 'reminder' ? 4 : 8}
                    value={body}
                    onChange={(e) => updateBody(e.target.value)}
                    // helperText="Tip: Add a signature so the user knows who is sending the message"
                    sx={{ mb: 2 }}
                />

                {/* === MESSAGE-ONLY SECTIONS === */}
                <When condition={mode === 'message'}>
                    {/* Push Notification Settings */}
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: !channels.includes('PUSH') ? 'text.disabled' : undefined }}>
                        Push Notification Settings
                    </Typography>
                    {!channels.includes('PUSH') && (
                        <Typography sx={{ fontSize: '0.875rem', mb: 2, color: GT_COLORS.textMuted, fontStyle: 'italic' }}>
                            Enable Push Notification as a delivery method to configure these settings
                        </Typography>
                    )}

                    <Box sx={{ mb: 4, opacity: !channels.includes('PUSH') ? 0.5 : 1, pointerEvents: !channels.includes('PUSH') ? 'none' : 'auto' }}>
                        {/* Send as Announcement Toggle */}
                        <Tooltip
                            title={formState.isAnnouncement === true
                                ? 'Message will show as sent from leadership'
                                : `Message will show as sent from: ${authUser?.fullname}`}
                            placement='top'
                            arrow
                            enterDelay={1000}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                p: 2,
                                backgroundColor: GT_COLORS.pageBg,
                                borderRadius: 2,
                                mb: 2
                            }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.isAnnouncement}
                                            onChange={(e) => updateFormState({ isAnnouncement: e.target.checked })}
                                            disabled={!channels.includes('PUSH')}
                                        />
                                    }
                                    label=""
                                    sx={{ m: 0 }}
                                />
                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body1" fontWeight={500} sx={{ color: GT_COLORS.textPrimary }}>
                                        Send as Announcement
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                                        Send anonymously from organization leadership instead of from your account
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>

                        {/* Critical Message Checkbox */}
                        <Tooltip
                            title={formState.isCritical === true
                                ? 'Message will be sent as critical'
                                : 'Message will be sent with normal priority'}
                            placement='bottom'
                            arrow
                            enterDelay={1000}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                p: 2,
                                backgroundColor: GT_COLORS.pageBg,
                                borderRadius: 2,
                                mb: 2
                            }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formState.isCritical}
                                            onChange={(e) => updateFormState({ isCritical: e.target.checked })}
                                            disabled={!channels.includes('PUSH')}
                                        />
                                    }
                                    label=""
                                    sx={{ m: 0, ml: 1 }}
                                />
                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body1" fontWeight={500} sx={{ color: GT_COLORS.amber }}>
                                        Send as Critical Message
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                                        High-priority delivery for emergencies and time-sensitive alerts
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    </Box>

                    {/* Schedule Section */}
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 2 }}>
                        Schedule Message
                    </Typography>

                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, mb: 4, py: 2, px: 4, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${GT_COLORS.border}` }}>
                        <Typography variant="body1" fontWeight={formState.messageType === 'IMMEDIATE' ? 600 : 400}>
                            Send Now
                        </Typography>

                        <Switch
                            checked={formState.messageType === 'SCHEDULED'}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    handleScheduleMessage();
                                } else {
                                    updateFormState({ messageType: 'IMMEDIATE' });
                                }
                            }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon fontSize="small" />
                            <Typography variant="body1" fontWeight={formState.messageType === 'SCHEDULED' ? 600 : 400}>
                                Schedule
                            </Typography>
                            <When condition={formState.messageType === 'SCHEDULED' && Boolean(getScheduledTimeDisplay())}>
                                <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                                    ({getScheduledDateDisplay()} {getScheduledTimeDisplay()})
                                </Typography>
                            </When>
                        </Box>
                    </Box>
                </When>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            color: GT_COLORS.textMuted,
                            borderColor: GT_COLORS.borderInput,
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isFormValid() || loading.createMessage || loading.updateMessage}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            backgroundColor: GT_COLORS.primary,
                            '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                        }}
                    >
                        {getActionButtonText()}
                    </Button>
                </Box>

                {/* Schedule Modal (message mode only) */}
                <When condition={mode === 'message'}>
                    <Dialog
                        open={showScheduleModal}
                        onClose={() => setShowScheduleModal(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary }}>Schedule message</Typography>
                            <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                                Eastern Time (US and Canada)
                            </Typography>
                            <IconButton onClick={() => setShowScheduleModal(false)} sx={{ color: GT_COLORS.textFaint }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent>
                            <Grid2 container spacing={3} sx={{ mt: 1 }}>
                                <Grid2 size={6}>
                                    <DatePicker
                                        label="Date"
                                        value={tempScheduledDate}
                                        onChange={(newValue) => setTempScheduledDate(newValue)}
                                        renderInput={(params) => (
                                            <TextField fullWidth {...params} />
                                        )}
                                    />
                                </Grid2>
                                <Grid2 size={6}>
                                    <TimePicker
                                        label="Time"
                                        value={tempScheduledTime}
                                        onChange={(newValue) => setTempScheduledTime(newValue)}
                                        renderInput={(params) => (
                                            <TextField fullWidth {...params} />
                                        )}
                                    />
                                </Grid2>
                            </Grid2>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setShowScheduleModal(false)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    color: GT_COLORS.textMuted,
                                    borderColor: GT_COLORS.borderInput,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleScheduleConfirm}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    backgroundColor: GT_COLORS.primary,
                                    '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                }}
                            >
                                Schedule Message
                            </Button>
                        </DialogActions>
                    </Dialog>
                </When>
            </Box>
        </LocalizationProvider>
    );
}
